import { lazy, useCallback, useEffect, useMemo, useState } from 'react';
import { Stack } from 'react-bootstrap';
import { useNavigate, useParams, useSearchParams } from 'react-router';

import LoadingIndicator from '@components/LoadingIndicator/LoadingIndicator';
import OrderFormContainer from '@components/RequisitionCreate/OrderFormContainer/OrderFormContainer';
import { DRAFT_ORDERS, PATIENTS } from '@constants/routes';
import { RerequisitionType } from '@customTypes/requisition';
import { useActiveMethylationReflexDraftForRequisition, usePatientDetail } from '@hooks';
import { getDraftOrderDetail } from '@store/actions';

const RequisitionJSONSchemaForm = lazy(
  () => import('@components/RequisitionCreate/RequisitionJSONSchemaForm/RequisitionJSONSchemaForm'),
);

export const OrderMethylationReanalysis: React.FC = () => {
  const params = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const patientId = useMemo(() => params?.id, [params]);
  const requisitionId = useMemo(() => params?.reqId, [params]);
  const productIds = useMemo(() => searchParams.getAll('product_ids'), [searchParams]);
  const { patientName, loading } = usePatientDetail(patientId);
  const [draftRedirectResolved, setDraftRedirectResolved] = useState(false);

  const { draftId: activeMethylationDraftId, refetch: refetchActiveMethylationDraft } =
    useActiveMethylationReflexDraftForRequisition(requisitionId);

  useEffect(() => {
    if (!activeMethylationDraftId || !patientId) {
      setDraftRedirectResolved(true);
      return;
    }

    let cancelled = false;
    setDraftRedirectResolved(false);

    getDraftOrderDetail(activeMethylationDraftId)
      .then(() => {
        if (!cancelled) {
          navigate(`${DRAFT_ORDERS}/${activeMethylationDraftId}`, {
            replace: true,
            state: { prevPath: `${PATIENTS}/${patientId}` },
          });
        }
      })
      .catch(async () => {
        if (!cancelled) {
          await refetchActiveMethylationDraft();
          setDraftRedirectResolved(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [activeMethylationDraftId, navigate, patientId, refetchActiveMethylationDraft]);

  const moveToPatientDetail = useCallback(() => {
    navigate(`/patients/${patientId}`);
  }, [patientId, navigate]);

  const breadcrumbs = useMemo(
    () => [
      {
        onClick: moveToPatientDetail,
        children: patientName || (loading ? 'Loading…' : ''),
      },
      {
        active: true,
        children: 'Order Methylation Reflex',
      },
    ],
    [moveToPatientDetail, patientName, loading],
  );

  if (activeMethylationDraftId && !draftRedirectResolved) {
    return (
      <Stack className='align-items-center py-5'>
        <LoadingIndicator />
      </Stack>
    );
  }

  return (
    <OrderFormContainer
      cancelReturnsToPatient
      enableSaveAsDraft
      rerequisitionFrom={requisitionId}
      rerequisitionType={RerequisitionType.METHYLATION_REFLEX}
      breadcrumbs={breadcrumbs}
      title='Order Methylation Reflex'
      onCancel={moveToPatientDetail}
    >
      {loading ? (
        <Stack className='align-items-center py-5'>
          <LoadingIndicator />
        </Stack>
      ) : (
        <RequisitionJSONSchemaForm
          initialProductIds={productIds}
          rerequisitionType={RerequisitionType.METHYLATION_REFLEX}
          selectedRequisitionId={requisitionId}
        />
      )}
    </OrderFormContainer>
  );
};
